import { DivideIcon } from "lucide-react";
import { useCallback } from "react";
import { T, RecordProps, TLShape, TLAsset, TLBinding, Editor } from "tldraw";

import {
  BlockBody,
  BlockContainer,
  BlockContainerShapeContext,
  NodeBlockHeader,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasInput,
  FieldSelect,
  OperatorSelect,
} from "@/components/canvas-editor/components/Inputs";
import { NodeDocs } from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  Dimensions,
  Field,
  metadataType,
  numberOperators,
  RatioCompareProps,
  RatioComparisonOperator,
  ratioComparisonOperators,
  RatioCompareShape,
  ratioCompareType,
  ratioFieldOptions,
} from "@/components/canvas-editor/shapes/shapes";
import { MIN_H, ToolStateNode } from "@/components/canvas-editor/utils";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { ListItem, UnorderedList } from "@/components/ui/typography";

const defaultDescription =
  "Compares one numeric attribute divided by another against a value.";

export function isRatioCompareShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is RatioCompareShape {
  return shape.type === ratioCompareType;
}

export class RatioCompareTool extends ToolStateNode {
  static override id = ratioCompareType;

  static override uiToolDef = (editor: Editor) => ({
    id: ratioCompareType,
    icon: DivideIcon,
    label: RatioCompareShapeUtil.docs.title,
    defaultColor: RatioCompareShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(ratioCompareType),
    docs: () => (
      <NodeDocs
        editor={editor}
        util={RatioCompareShapeUtil}
        component={RatioCompare}
      />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: ratioCompareType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

export class RatioCompareShapeUtil extends BindableUtil<RatioCompareShape> {
  static override migrations = createMigrations(ratioCompareType);
  static override type = ratioCompareType;

  static override docs = {
    defaultColor: "teal" as const,
    title: "Ratio Comparison",
    description: (
      <div>
        <div>{defaultDescription}</div>
        <UnorderedList className="ml-1">
          <ListItem className="my-1">
            Use it for things a single threshold cannot express — “at least twice
            as many followers as accounts followed”, rather than “at least 500
            followers”.
          </ListItem>
          <ListItem className="my-1">
            Only numeric attributes can be divided, so the lists here are shorter
            than in Attribute Comparison.
          </ListItem>
          <ListItem className="my-1">
            <span className="font-black">Nothing matches when the bottom
            value is zero.</span>{" "}
            An account that follows nobody has a follow count of 0, and there is
            no meaningful ratio to compare — so it is excluded rather than
            treated as infinite.
          </ListItem>
          <ListItem className="my-1">
            There is no “is exactly” — a ratio is a decimal, and asking for an
            exact match would almost always return nothing.
          </ListItem>
        </UnorderedList>
      </div>
    ),
  };

  override getType() {
    return ratioCompareType;
  }

  static override props: RecordProps<RatioCompareShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    // @ts-expect-error We want users to be able to select any field
    numerator: T.string,
    // @ts-expect-error We want users to be able to select any field
    denominator: T.string,
    value: T.nullable(T.or(T.string, T.number)),
    operator: T.literalEnum(...ratioComparisonOperators),
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 300,
      minW: 440,
      h: MIN_H,
      // followers ÷ follows: the ratio this operator was asked for.
      numerator: (ratioFieldOptions.find((f) =>
        f.value.endsWith("followers_count"),
      )?.value ?? ratioFieldOptions[0]!.value) as Field,
      denominator: (ratioFieldOptions.find((f) =>
        f.value.endsWith("follows_count"),
      )?.value ?? ratioFieldOptions[0]!.value) as Field,
      value: 2,
      operator: ratioComparisonOperators[0],
      metadata: undefined,
    };
  }

  override component(shape: RatioCompareShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <RatioCompare
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

function RatioCompare({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: RatioCompareShape;
  className?: string;
  updateShape: (
    shape: RatioCompareShape,
    props: Partial<RatioCompareProps>,
  ) => void;
}) {
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        className={className}
        variant={
          shape.props.metadata?.color || RatioCompareShapeUtil.docs.defaultColor
        }
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
      >
        <NodeBlockHeader props={shape.props} docs={RatioCompareShapeUtil.docs} />
        <BlockBody>
          <SimpleTooltip message="The attribute on top of the division.">
            <FieldSelect
              value={shape.props.numerator}
              validOperators={numberOperators}
              onValueChange={(v) =>
                updateShape(shape, { numerator: v as Field })
              }
            />
          </SimpleTooltip>
          <span className="text-slate-dim px-1">÷</span>
          <SimpleTooltip message="The attribute underneath. Nothing matches when this is zero.">
            <FieldSelect
              value={shape.props.denominator}
              validOperators={numberOperators}
              onValueChange={(v) =>
                updateShape(shape, { denominator: v as Field })
              }
            />
          </SimpleTooltip>
          <OperatorSelect
            value={shape.props.operator}
            validOperators={[...ratioComparisonOperators]}
            onValueChange={(v) =>
              v &&
              updateShape(shape, { operator: v as RatioComparisonOperator })
            }
          />
          <SimpleTooltip message="The value to compare the ratio against.">
            <CanvasInput
              aria-label="Ratio value"
              value={shape.props.value ?? ""}
              className="flex-1"
              placeholder="2"
              onValueChange={(value) => updateShape(shape, { value })}
              required={false}
            />
          </SimpleTooltip>
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
